import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { graphqlClient } from '@/services/core/graphqlClient';

export interface PatientAppointment {
  appointment_date_end_time: string;
  appointment_date_start_time: string;
  appointment_id: number;
  appointment_type: number; // 1 = Virtual, 2 = In-Person
  clinic_facility_id: number | null;
  clinic_id: number;
  patient_name: string;
  user_id: number | null;
  enrollment_patient_id: number;
  clinic_name: string;
  clinic_timezone: string;
  user_timezone: string | null;
  facility_name: string | null;
  user_name: string;
}

const GET_SCHEDULED_APPOINTMENTS_OF_PATIENT = `
  query getScheduledAppointmentsOfPatient($patientId: Int!) {
    vw_patient_appointment_details(
      where: {enrollment_patient_id: {_eq: $patientId}}, 
      order_by: {appointment_date_end_time: desc}
    ) {
      appointment_date_end_time
      appointment_date_start_time
      appointment_id
      appointment_type
      clinic_facility_id
      clinic_id
      patient_name
      user_id
      enrollment_patient_id
      clinic_name
      clinic_timezone
      user_timezone
      facility_name
      user_name
    }
  }
`;

export const usePatientAppointments = () => {
  const { hasuraToken } = useAuth();
  const [appointments, setAppointments] = useState<PatientAppointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAppointments = useCallback(async (patientId: number) => {
    if (!hasuraToken || !patientId) {
      setAppointments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await graphqlClient.query<{
        vw_patient_appointment_details: PatientAppointment[];
      }>(
        GET_SCHEDULED_APPOINTMENTS_OF_PATIENT,
        { patientId },
        hasuraToken
      );

      setAppointments(response.data?.vw_patient_appointment_details || []);
    } catch (err) {
      console.error('Error fetching patient appointments:', err);
      setError('Failed to fetch appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken]);

  const clearAppointments = useCallback(() => {
    setAppointments([]);
    setError(null);
  }, []);

  return {
    appointments,
    loading,
    error,
    fetchAppointments,
    clearAppointments
  };
};
